import s from "./styles";
import React, { useCallback, useMemo, useState } from "react";
import PieChartCustomers from "components/InformCustomersReport/PieChartCustomers";
import TopWidgetInformation from "components/common/TopWidgetInformation";
import List from "components/common/ListWidget";
import moment from "moment";
import { toPng } from "html-to-image";
import { useBusinessContext } from "BusinessApp";
import { utils, writeFile } from "xlsx";

import BagIcon from "components/icons/BagIcon";

import Bill from "components/icons/Bill";
import Moneybag from "components/icons/Moneybag";
import "../../index.css";
import { useTranslation } from "react-i18next";
import TopMenu from "components/Reports/TopMenu";
import useCustomers from "graphql/useCustomers";
import useOrders from "graphql/useOrders";
import { getDateHeader } from "helpers/dataHelper";
import DocumentPDF from "./DocumentPDF";
import { useOrderItemSummary } from "graphql/useOrderItemSummary";
import { printDocumentPDF } from "helpers/utils";

function ReportCustomers() {
  const { t } = useTranslation();
  const { storeId, currencyFormat } = useBusinessContext();
  const [date, setDate] = useState({
    value: moment(),
    periodType: "date",
    range: { start: moment().startOf("day"), end: moment().endOf("day") },
  });

  const firstGraph = React.useRef(null);

  const allDate = `${
    date.periodType === "date"
      ? t("containers.ReportsTaxes.allday")
      : date.periodType === "week"
      ? t("containers.ReportsTaxes.allWeek")
      : date.periodType === "month"
      ? t("containers.ReportsTaxes.allMonth")
      : t("containers.ReportsTaxes.allYear")
  }`;

  const [sorting, setSorting] = useState({ columnId: "earnings", order: 1 });

  const { customers, loading: loading1 } = useCustomers();
  const { orders, loading: loading2 } = useOrders(storeId, date?.range);

  const { orderItemSummary, loading: loading3 } = useOrderItemSummary({
    storeId,
    start: date.range.start,
    end: date.range.end,
    limit: 3,
    order: -1,
    sort: "quantity",
    withCustomer: true,
  });

  const { dataItemsMostBuysExcel, dataItemsMostBuys } = useMemo(() => {
    const dataItemsMostBuysExcel = orderItemSummary.map((item) => [
      item._id.itemName,
      item._id.unitPrice,
      item.quantity,
      item.total,
    ]);

    const dataItemsMostBuys = orderItemSummary.map((item) => [
      item._id.itemName,
      currencyFormat(item._id.unitPrice),
      item.quantity,
      currencyFormat(item.total),
    ]);

    return {
      dataItemsMostBuys,
      dataItemsMostBuysExcel,
    };
  }, [orderItemSummary]);

  const { customersWithMostPurchases, customersWithMostPurchasesExcel } =
    useMemo(() => {
      const customerSales = {};

      orders.forEach((sale) => {
        const { customerName, total, quantity, payment } = sale;
        const totalNum = parseFloat(total);
        const quantityNum = parseFloat(quantity);

        if (customerName) {
          if (!customerSales[customerName]) {
            customerSales[customerName] = {
              total: 0,
              items: 0,
            };
          }

          if (payment && payment.includes("refund")) {
            customerSales[customerName].total =
              (customerSales[customerName].total || 0) - totalNum;
            customerSales[customerName].items =
              (customerSales[customerName].items || 0) - quantityNum;
          } else {
            customerSales[customerName].total =
              (customerSales[customerName].total || 0) + totalNum;
            customerSales[customerName].items =
              (customerSales[customerName].items || 0) + quantityNum;
          }
        }
      });

      const sortedCustomers = Object.entries(customerSales)
        .sort(([, a], [, b]) => b.total - a.total)
        .map(([customerName, sales]) => ({
          customerName,
          items: sales.items,
          total: sales.total,
        }))
        .filter((item) => item.customerName);

      const customersWithMostPurchases = sortedCustomers
        .slice(0, 3)
        .map((item) => [
          item.customerName,
          item.items,
          currencyFormat(item.total),
        ]);

      const customersWithMostPurchasesExcel = sortedCustomers.map((item) => [
        item.customerName,
        item.items,
        item.total,
      ]);

      return {
        customersWithMostPurchases,
        customersWithMostPurchasesExcel,
      };
    }, [orders]);

  function getMoreRepeatedIds(orders) {
    const ids = [];
    orders.forEach((order) => {
      order?.items?.forEach((item) => {
        if (item in ids) {
          ids[item]++;
        } else {
          ids[item] = 1;
        }
      });
    });

    let idsMostRepeated = [];
    let maxReps = 0;
    for (const id in ids) {
      const repeats = ids[id];
      if (repeats > maxReps) {
        idsMostRepeated = [id];
        maxReps = repeats;
      } else if (repeats === maxReps) {
        idsMostRepeated.push(id);
      }
    }

    return idsMostRepeated.map((id) => ({ id, repeats: ids[id] }));
  }

  getMoreRepeatedIds(orders);

  // **************************

  const dataHeader = useMemo(() => {
    const currentMonth = new Date().getMonth();
    const filteredUsersNew = customers.filter((item) => {
      const itemMonth = new Date(item.creationDatetime).getMonth();
      return itemMonth === currentMonth;
    });

    const ordersWithTypeUser = orders.map((item) => {
      const user = customers.find((customer) => {
        if (item.customerId === customer._id) {
          return customer;
        }
      });
      return {
        ...item,
        userType: user?.tags[0],
      };
    });

    const ordersTotalWithCustomer = [];
    const ordersTotalWithoutCustomer = [];

    const ordersTotalWithCustomerBusiness = [];
    const ordersTotalWithoutCustomerPerson = [];

    ordersWithTypeUser.forEach((item) => {
      if (item.customerId) {
        ordersTotalWithCustomer.push(item);
      } else {
        ordersTotalWithoutCustomer.push(item);
      }
    });

    ordersWithTypeUser.forEach((item) => {
      if (item.userType === "business") {
        ordersTotalWithCustomerBusiness.push(item);
      } else if (item.userType === "person") {
        ordersTotalWithoutCustomerPerson.push(item);
      }
    });

    const customerSales = ordersTotalWithCustomer.reduce((a, b) => {
      let total = parseFloat(b.total);
      if (b.extraAmount1) {
        total -= parseFloat(b.extraAmount1);
      }
      if (b.payment && !b.payment.includes("refund")) {
        return a + total;
      } else if (b.type === "cre") {
        return a - total;
      }
      return a;
    }, 0);

    const notCustomerSales = ordersTotalWithoutCustomer.reduce((a, b) => {
      let total = parseFloat(b.total);
      if (b.extraAmount1) {
        total -= parseFloat(b.extraAmount1);
      }
      if (b.payment && !b.payment.includes("refund")) {
        return a + total;
      } else if (b.type === "cre") {
        return a - total;
      }
      return a;
    }, 0);

    const customerSalesBusiness = ordersTotalWithCustomerBusiness.reduce(
      (a, b) => {
        let total = parseFloat(b.total);
        if (b.extraAmount1) {
          total -= parseFloat(b.extraAmount1);
        }
        if (b.payment && !b.payment.includes("refund")) {
          return a + total;
        } else if (b.type === "cre") {
          return a - total;
        }
        return a;
      },
      0
    );

    const customerSalesPerson = ordersTotalWithoutCustomerPerson.reduce(
      (a, b) => {
        if (b.payment && !b.payment.includes("refund")) {
          return a + parseFloat(b.total);
        } else if (b.type === "cre") {
          return a - parseFloat(b.total);
        }
        return a;
      },
      0
    );

    return {
      totalUsers: customers.length,
      newUsers:
        date.periodType === "date" || date.periodType === "month"
          ? filteredUsersNew.length
          : customers.length,
      customerSales: customerSales,
      notCustomerSales: notCustomerSales,
      customerSalesBusiness: customerSalesBusiness,
      customerSalesPerson: customerSalesPerson,
    };
  }, [customers, date.periodType, orders]);

  const exportFileExcel = useCallback(() => {
    const worksheetData = [
      [
        `${t("containers.ReportsCustomers.reportsCustomerFor")} ${getDateHeader(
          date.range.start,
          date.range.end
        )}`,
      ],
      [
        t("containers.ReportsCustomers.newCustomers"),
        t("containers.ReportsCustomers.allCustomers"),
        t("containers.ReportsCustomers.customerSales"),
        t("containers.ReportsCustomers.noCustomerSales"),
      ],
      [
        dataHeader.newUsers || "-",
        dataHeader.totalUsers || "-",
        dataHeader.customerSales || "-",
        dataHeader.notCustomerSales || "-",
      ],
      [],
      [t("containers.ReportsCustomers.customerTypes")],
      [
        t("containers.ReportsCustomers.customer"),
        dataHeader.customerSalesPerson,
      ],
      [
        t("containers.ReportsCustomers.business"),
        dataHeader.customerSalesBusiness,
      ],
      [t("containers.ReportsCustomers.NoDefined"), dataHeader.notCustomerSales],
      [],
      [t("containers.ReportsCustomers.customerWithMoreBuys")],
      [
        t("containers.ReportsCustomers.client"),
        t("containers.ReportsCustomers.buyItems"),
        t("containers.ReportsCustomers.totalSales"),
      ],
      ...customersWithMostPurchasesExcel,
      [],
      [t("containers.ReportsCustomers.itemsMostBuys")],
      [
        t("containers.ReportsCustomers.item"),
        t("containers.ReportsCustomers.itemPrice"),
        t("containers.ReportsCustomers.bought"),
        t("containers.ReportsCustomers.totalBought"),
      ],
      ...dataItemsMostBuysExcel,
    ];

    const workbook = utils.book_new();
    const sheetName = `${t("containers.ReportsCustomers.reportsCustomer")}`;
    const worksheet = utils.aoa_to_sheet(worksheetData);
    utils.book_append_sheet(workbook, worksheet, sheetName);

    writeFile(
      workbook,
      `${t("containers.ReportsCustomers.reportsCustomer")}.xlsx`
    );
  }, [
    t,
    date.range.start,
    date.range.end,
    dataHeader.newUsers,
    dataHeader.totalUsers,
    dataHeader.customerSales,
    dataHeader.notCustomerSales,
    dataHeader.customerSalesPerson,
    dataHeader.customerSalesBusiness,
    customersWithMostPurchasesExcel,
    dataItemsMostBuysExcel,
  ]);

  const getImageGraph = useCallback(async () => {
    const img1 = await toPng(firstGraph.current, { cacheBust: true });

    await printDocumentPDF(
      <DocumentPDF
        data={{
          dateHeader: getDateHeader(date.range.start, date.range.end)[0],
          header: {
            ...dataHeader,
            customerSales: currencyFormat(dataHeader.customerSales),
            notCustomerSales: currencyFormat(dataHeader.notCustomerSales),
          },
          customersWithMostPurchases,
          dataItemsMostBuys,
          img1,
        }}
      />,
      `${t("containers.ReportsCustomers.reportsCustomer")}.pdf`
    );
  }, [
    customersWithMostPurchases,
    dataItemsMostBuys,
    dataHeader,
    date.range.end,
    date.range.start,
    t,
  ]);

  const loading = loading1 || loading2 || loading3;

  return (
    <s.PageContainer>
      <TopMenu
        loading={loading}
        value={date.value}
        onChange={(value, periodType, range) => {
          setDate({ value, periodType, range });
        }}
        exportFileExcel={exportFileExcel}
        getImageGraph={getImageGraph}
        disabled={
          customers.length === 0 || Object.values(dataHeader).length === 0
        }
      />

      <TopWidgetInformation
        elements={[
          {
            value: dataHeader.newUsers || "-",
            text: t("containers.ReportsCustomers.newCustomers"),
            icon: <BagIcon />,
          },
          {
            value: dataHeader.totalUsers || "-",
            text: t("containers.ReportsCustomers.allCustomers"),
            icon: <Bill />,
          },
          {
            value: currencyFormat(dataHeader.customerSales),
            text: t("containers.ReportsCustomers.customerSales"),
            icon: <Moneybag />,
          },
          {
            value: currencyFormat(dataHeader.notCustomerSales),
            text: t("containers.ReportsCustomers.noCustomerSales"),
            icon: <Moneybag />,
          },
        ]}
      />
      <s.Container>
        <div style={{ width: "auto", height: "259" }}>
          <PieChartCustomers
            emptyData={orders.length === 0}
            dataHeader={dataHeader}
            loading={loading}
            firstGraph={firstGraph}
          />
        </div>
        <s.ChartComponentsWrapper
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
          }}
        >
          <List
            width="720px"
            items={customersWithMostPurchases}
            title={t("containers.ReportsCustomers.customerWithMoreBuys")}
            allDate={allDate}
            columns={[
              {
                title: t("containers.ReportsCustomers.client"),
              },
              {
                title: t("containers.ReportsCustomers.buyItems"),
              },
              {
                title: t("containers.ReportsCustomers.totalSales"),
              },
            ]}
            tableStyle={{ gridTemplateColumns: "30fr 21fr 17fr " }}
            sorting={sorting}
            changeSorting={(columnId, order) => setSorting({ columnId, order })}
            loading={loading}
          />
          <List
            width="720px"
            items={dataItemsMostBuys}
            title={t("containers.ReportsCustomers.itemsMostBuys")}
            allDate={allDate}
            columns={[
              {
                title: t("containers.ReportsCustomers.item"),
              },
              {
                title: t("containers.ReportsCustomers.itemPrice"),
              },
              {
                title: t("containers.ReportsCustomers.bought"),
              },
              {
                title: t("containers.ReportsCustomers.totalBought"),
              },
            ]}
            tableStyle={{ gridTemplateColumns: "26fr 21fr 19fr 17fr " }}
            sorting={sorting}
            changeSorting={(columnId, order) => setSorting({ columnId, order })}
            loading={loading}
          />
        </s.ChartComponentsWrapper>
      </s.Container>
    </s.PageContainer>
  );
}

export default ReportCustomers;
